import { useEffect } from "react";
import Home from "./Home";

/**
 * Mounts the full Home landing page and auto-scrolls to a given section.
 *
 * Why this pattern (vs separate pages):
 *   The user's requested sitemap URLs (/pricing, /about, /contact) map to
 *   content that already lives as anchor sections on the landing page
 *   (#calculator, #how-it-works, #contact). Rather than duplicating copy
 *   into thin standalone pages — which hurts SEO via duplicate-content —
 *   we serve the full landing page (already canonicalised to `/` via
 *   <link rel="canonical"> in public/index.html) and simply scroll the
 *   visitor to the relevant section on mount.
 *
 * Google behaviour:
 *   Googlebot indexes the content at / and treats these 3 URLs as
 *   alternate entry points thanks to the canonical tag — no duplicate-
 *   content penalty, ranking signals consolidate to /.
 */
export default function HomeSection({ anchor }) {
  useEffect(() => {
    if (!anchor) return;
    const tryScroll = () => {
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };
    // Landing sections mount after hydration — retry a few frames.
    if (tryScroll()) return;
    const t1 = setTimeout(tryScroll, 150);
    const t2 = setTimeout(tryScroll, 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [anchor]);

  return <Home />;
}
